export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatRelativeTime(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export function resultLetter(result: string | null): "W" | "L" | "½" | "—" {
  if (!result) return "—";
  const r = result.toLowerCase();
  if (r === "win" || r === "1-0" || r === "0-1") {
    return r === "0-1" ? "L" : "W";
  }
  if (r === "loss") return "L";
  if (r.startsWith("draw") || r === "1/2-1/2" || r === "½-½") return "½";
  return "—";
}

// Raw time controls look like "300", "300+5" (chess.com seconds) or "-"
// (correspondence). Produce e.g. "BLITZ 5+0", "RAPID 10+5", "CORRESPONDENCE".
export function formatTimeControl(tc: string | null): string {
  if (!tc) return "?";
  if (tc === "-" || tc.startsWith("1/")) return "CORRESPONDENCE";
  const m = tc.match(/^(\d+)(?:\+(\d+))?$/);
  if (!m) return tc.toUpperCase();
  const base = parseInt(m[1], 10);
  const inc = m[2] ? parseInt(m[2], 10) : 0;
  const bucket =
    base < 180 ? "BULLET" : base < 600 ? "BLITZ" : base < 1500 ? "RAPID" : "CLASSICAL";
  const minutes = base % 60 === 0 ? `${base / 60}` : (base / 60).toFixed(1);
  return `${bucket} ${minutes}+${inc}`;
}

// Sort key for a time control (base seconds; correspondence sorts last/longest).
export function timeControlSeconds(tc: string | null): number {
  if (!tc) return -1;
  if (tc === "-" || tc.startsWith("1/")) return Number.MAX_SAFE_INTEGER;
  const m = tc.match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : -1;
}

// chess.com stores an openings URL in `eco` (not a short code). Turn either a
// stored opening name or that URL into a clean human label.
export function openingLabel(g: { opening: string | null; eco: string | null }): string {
  if (g.opening) return g.opening;
  const eco = g.eco;
  if (!eco) return "—";
  if (!eco.includes("/openings/")) return eco; // a real ECO code like "B20"
  const slug = eco.split("/openings/")[1] ?? "";
  // Drop the trailing move sequence (e.g. "-3.Bf4-Nf6", "-1...d5") and clean up.
  const name = slug
    .replace(/-?\d+\.{0,3}[A-Za-z].*$/, "")
    .replace(/-/g, " ")
    .trim();
  return name || "—";
}

export function initialsFrom(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return "??";
  return parts.slice(0, 2).map((s) => s[0]?.toUpperCase() ?? "").join("") || "??";
}
